import {
  createContext,
  useContext,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import type { RecapModeConfig, ColumnDef } from "../config/types";
import { STANDALONE_COLUMNS } from "../config/columns";
import type { RecapStateReturn } from "../hooks/useRecapState";
import type { RecapDataReturn } from "../hooks/useRecapData";
import type { RecapFilteringReturn } from "../hooks/useRecapFiltering";
import type { RecapSelectionReturn } from "../hooks/useRecapSelection";
import type { ExcelFilterItem } from "@/components/stocks/useExcelFilter";
import type {
  ChatPanelActions,
  ChatPanelStackReturn,
} from "../hooks/useChatPanelStack";
import {
  useConflictHighlight,
  type ConflictHighlightApi,
} from "../hooks/useConflictHighlight";
import { ChatPanelSelectionStoreContext } from "../stores/chatPanelSelectionStore";
import { useRenderCount } from "../utils/perfLogger";

// ---------------------------------------------------------------------------
// Split contexts — each slice updates independently, preventing cascading
// re-renders across unrelated consumers.
// ---------------------------------------------------------------------------

interface RecapModeValue {
  /** Behavioral mode, or `null` for sibling workspaces (transport) that
   * are neither sales nor purchases. Shared components treat `null` as the
   * sales default (every branch checks `=== "purchases"`). */
  mode: RecapModeConfig | null;
  /** Route prefix this workspace was mounted under — `/recap`,
   * `/recap_transport`, `/purchases`, etc. Used by row/header navigation
   * + URL writes so shared components don't hardcode `/recap`. */
  basePath: string;
  columns: ColumnDef[];
  focusNewOrderSku: (preOrderId: string) => void;
}

const RecapModeContext = createContext<RecapModeValue | null>(null);
const RecapStateInternalContext = createContext<RecapStateReturn | null>(null);
const RecapDataInternalContext = createContext<RecapDataReturn | null>(null);
const RecapFilteringInternalContext =
  createContext<RecapFilteringReturn | null>(null);
const SelectionContext = createContext<RecapSelectionReturn | null>(null);
type AccountSubItemsFn = (sourceType: number) => ExcelFilterItem[];
const EMPTY_SUB_ITEMS: AccountSubItemsFn = () => [];
const AccountSubItemsContext =
  createContext<AccountSubItemsFn>(EMPTY_SUB_ITEMS);
const ChatPanelContext = createContext<ChatPanelStackReturn | null>(null);
/**
 * Stable subset of the chat panel — its identity never changes across
 * renders, so consumers that only need to *call* the panel (open, close,
 * highlight, …) don't re-render on panel state flips. Use
 * `useChatPanelContext()` if you also need to read state reactively.
 */
const ChatPanelActionsContext = createContext<ChatPanelActions | null>(null);
const ConflictHighlightContext = createContext<ConflictHighlightApi | null>(
  null,
);

// Legacy combined type — kept for backward compatibility with tests
export interface RecapContextType {
  mode: RecapModeConfig | null;
  basePath: string;
  columns: ColumnDef[];
  state: RecapStateReturn;
  data: RecapDataReturn;
  filtering: RecapFilteringReturn;
  focusNewOrderSku: (preOrderId: string) => void;
  accountSubItems: (sourceType: number) => ExcelFilterItem[];
}

/**
 * Hosts the chat panel stack contexts independently of RecapProvider so the
 * chat panel can be lifted into RecapWorkspaceLayout — the panel mounts
 * ONCE and persists across navigation between /recap (list) and /recap/:id
 * (focus), each of which mounts its own RecapProvider for route-scoped
 * state/data/filtering.
 */
export function ChatPanelStackProvider({
  children,
  chatPanel,
}: {
  children: ReactNode;
  chatPanel: ChatPanelStackReturn;
}) {
  return (
    <ChatPanelSelectionStoreContext.Provider value={chatPanel.selectionStore}>
      <ChatPanelActionsContext.Provider value={chatPanel.actions}>
        <ChatPanelContext.Provider value={chatPanel}>
          {children}
        </ChatPanelContext.Provider>
      </ChatPanelActionsContext.Provider>
    </ChatPanelSelectionStoreContext.Provider>
  );
}

export function RecapProvider({
  children,
  value,
  selection,
}: {
  children: ReactNode;
  value: RecapContextType;
  selection: RecapSelectionReturn;
}) {
  useRenderCount("RecapProvider");

  // Log which parts of context value changed (helps identify cascading re-render causes)
  const prevRef = useRef(value);
  if (
    typeof window !== "undefined" &&
    localStorage.getItem("RECAP_PERF") === "1"
  ) {
    const prev = prevRef.current;
    const changes: string[] = [];
    if (prev.mode !== value.mode) changes.push("mode");
    if (prev.columns !== value.columns) changes.push("columns");
    if (prev.state !== value.state) changes.push("state");
    if (prev.data !== value.data) changes.push("data");
    if (prev.filtering !== value.filtering) changes.push("filtering");
    if (changes.length > 0) {
      console.log(
        `%c[PERF] RecapContext value changed: ${changes.join(", ")}`,
        "color: #e06000; font-weight: bold",
      );
    }
  }
  prevRef.current = value;

  // Memoize each slice independently so only affected consumers re-render
  const modeValue = useMemo<RecapModeValue>(
    () => ({
      mode: value.mode,
      basePath: value.basePath,
      columns: value.columns,
      focusNewOrderSku: value.focusNewOrderSku,
    }),
    [value.mode, value.basePath, value.columns, value.focusNewOrderSku],
  );

  const accountSubItems = value.accountSubItems;

  // Conflict highlight is provider-scoped so the 5s TTL state lives as long
  // as the recap is mounted. Own slice → only the rows with the ring (and
  // the BatchMerge handler that writes it) re-render on flag/expire.
  const conflictHighlight = useConflictHighlight();

  return (
    <RecapModeContext.Provider value={modeValue}>
      <RecapStateInternalContext.Provider value={value.state}>
        <RecapDataInternalContext.Provider value={value.data}>
          <RecapFilteringInternalContext.Provider value={value.filtering}>
            <AccountSubItemsContext.Provider value={accountSubItems}>
              <SelectionContext.Provider value={selection}>
                <ConflictHighlightContext.Provider value={conflictHighlight}>
                  {children}
                </ConflictHighlightContext.Provider>
              </SelectionContext.Provider>
            </AccountSubItemsContext.Provider>
          </RecapFilteringInternalContext.Provider>
        </RecapDataInternalContext.Provider>
      </RecapStateInternalContext.Provider>
    </RecapModeContext.Provider>
  );
}

export function useConflictHighlightContext(): ConflictHighlightApi {
  const ctx = useContext(ConflictHighlightContext);
  if (!ctx) {
    throw new Error(
      "useConflictHighlightContext must be used within RecapProvider",
    );
  }
  return ctx;
}

/** Returns the highlight API when inside RecapProvider, null otherwise.
 * Used by components that render in both recap and chat (e.g. PreOrderSection
 * is mounted standalone inside the chat panel for proposal preview). */
export function useConflictHighlightContextOptional(): ConflictHighlightApi | null {
  return useContext(ConflictHighlightContext);
}

// ---------------------------------------------------------------------------
// Convenience hooks — each reads from its own context (no cross-contamination)
// ---------------------------------------------------------------------------

export function useRecapMode(): RecapModeConfig | null {
  const ctx = useContext(RecapModeContext);
  if (!ctx) throw new Error("useRecapMode must be used within RecapProvider");
  return ctx.mode;
}

/**
 * Like `useRecapMode` but returns `null` outside a RecapProvider instead of
 * throwing. For components/hooks that render both inside the recap workspace
 * and standalone (e.g. shared edit hooks reused by the mobile proposal flow) —
 * lets them tag analytics with the workspace mode when present, omit it when not.
 */
export function useRecapModeOptional(): RecapModeConfig | null {
  const ctx = useContext(RecapModeContext);
  return ctx?.mode ?? null;
}

/**
 * Returns the route prefix the workspace was mounted under (`/recap`,
 * `/recap_transport`, `/purchases`, …). Navigation that links to "this
 * workspace's focus view" should compose `${basePath}/${preOrderId}` rather
 * than hardcoding `/recap/${preOrderId}`.
 */
export function useRecapBasePath(): string {
  const ctx = useContext(RecapModeContext);
  if (!ctx)
    throw new Error("useRecapBasePath must be used within RecapProvider");
  return ctx.basePath;
}

export function useRecapColumns(): ColumnDef[] {
  const ctx = useContext(RecapModeContext);
  if (!ctx)
    throw new Error("useRecapColumns must be used within RecapProvider");
  return ctx.columns;
}

/**
 * Returns columns from RecapContext if available, or STANDALONE_COLUMNS as fallback.
 * Use this in components that need to work both inside and outside RecapProvider (e.g. FlowsTable in chat).
 */
export function useRecapColumnsOptional(): ColumnDef[] {
  const ctx = useContext(RecapModeContext);
  return ctx?.columns ?? STANDALONE_COLUMNS;
}

export function useFocusNewOrderSku(): (preOrderId: string) => void {
  const ctx = useContext(RecapModeContext);
  if (!ctx)
    throw new Error("useFocusNewOrderSku must be used within RecapProvider");
  return ctx.focusNewOrderSku;
}

export function useRecapStateContext(): RecapStateReturn {
  const ctx = useContext(RecapStateInternalContext);
  if (!ctx)
    throw new Error("useRecapStateContext must be used within RecapProvider");
  return ctx;
}

export function useRecapDataContext(): RecapDataReturn {
  const ctx = useContext(RecapDataInternalContext);
  if (!ctx)
    throw new Error("useRecapDataContext must be used within RecapProvider");
  return ctx;
}

/**
 * Returns recap data if inside RecapProvider, null otherwise.
 * Use in components that work both inside and outside recap (e.g. ProductCell in chat).
 */
export function useRecapDataContextOptional(): RecapDataReturn | null {
  return useContext(RecapDataInternalContext);
}

export function useRecapFilteringContext(): RecapFilteringReturn {
  const ctx = useContext(RecapFilteringInternalContext);
  if (!ctx)
    throw new Error(
      "useRecapFilteringContext must be used within RecapProvider",
    );
  return ctx;
}

/**
 * Returns recap state if inside RecapProvider, null otherwise. Use in
 * components that work both inside the recap list view and on standalone
 * routes (e.g. ChatPanel mounted from RecapWorkspaceLayout above both
 * /recap and /recap/:id — only /recap mounts RecapProvider).
 */
export function useRecapStateContextOptional(): RecapStateReturn | null {
  return useContext(RecapStateInternalContext);
}

/**
 * Returns recap filtering if inside RecapProvider, null otherwise. Same
 * rationale as `useRecapStateContextOptional` — chat-panel features that
 * only make sense in the list-view context (e.g. the date/filter mismatch
 * banner in ConversationView) can no-op gracefully when this returns null.
 */
export function useRecapFilteringContextOptional(): RecapFilteringReturn | null {
  return useContext(RecapFilteringInternalContext);
}

export function useAccountSubItemsContext(): AccountSubItemsFn {
  return useContext(AccountSubItemsContext);
}

export function useRecapSelectionContext(): RecapSelectionReturn {
  const context = useContext(SelectionContext);
  if (!context) {
    throw new Error(
      "useRecapSelectionContext must be used within RecapProvider",
    );
  }
  return context;
}

export function useChatPanelContext(): ChatPanelStackReturn {
  const context = useContext(ChatPanelContext);
  if (!context) {
    throw new Error("useChatPanelContext must be used within ChatPanelContext");
  }
  return context;
}

/**
 * Stable subset of chat-panel — actions only, no reactive state. Identity
 * never changes, so consumers don't re-render when panel state flips. Use
 * in click/effect handlers (PartnerGroup, ProductGroup, PartnerCell, …).
 * For reactive state (e.g. button labels that depend on `isPanelOpen`),
 * use `useChatPanelContext()` instead.
 */
export function useChatPanelActions(): ChatPanelActions {
  const actions = useContext(ChatPanelActionsContext);
  if (!actions) {
    throw new Error("useChatPanelActions must be used within RecapProvider");
  }
  return actions;
}

/**
 * Same as `useChatPanelActions` but returns null outside RecapProvider —
 * use for components that can be rendered both inside recap and in standalone
 * contexts (e.g. PreOrderSection in chat).
 */
export function useChatPanelActionsOptional(): ChatPanelActions | null {
  return useContext(ChatPanelActionsContext);
}

/**
 * Returns the chat panel stack if inside RecapProvider, null otherwise.
 * Use in components that work both inside and outside recap (e.g. ProposalCard
 * on /ai-proposals or in the AI chat panel).
 */
export function useChatPanelContextOptional(): ChatPanelStackReturn | null {
  return useContext(ChatPanelContext);
}

// Legacy combined hook — avoid in new code, prefer specific hooks above
export function useRecapContext(): RecapContextType {
  const modeCtx = useContext(RecapModeContext);
  const state = useContext(RecapStateInternalContext);
  const data = useContext(RecapDataInternalContext);
  const filtering = useContext(RecapFilteringInternalContext);
  if (!modeCtx || !state || !data || !filtering) {
    throw new Error("useRecapContext must be used within RecapProvider");
  }
  const accountSubItems = useContext(AccountSubItemsContext);
  return {
    mode: modeCtx.mode,
    basePath: modeCtx.basePath,
    columns: modeCtx.columns,
    state,
    data,
    filtering,
    focusNewOrderSku: modeCtx.focusNewOrderSku,
    accountSubItems,
  };
}
